import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, UserPlus, Trash2, User, Phone, Edit2, Tag, Briefcase, Building2, UsersRound, X, Mail, Search, RotateCcw, AlertCircle } from 'lucide-react';

function ContactsManager() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', number: '', email: '', role_id: '', agency_id: '', people_client_id: '', people_team_id: '', workspace_ids: [] });
  const [roles, setRoles] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [peopleClients, setPeopleClients] = useState([]);
  const [peopleTeams, setPeopleTeams] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [editingContact, setEditingContact] = useState(null);
  
  // Search & dynamic filtering states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedAgency, setSelectedAgency] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');

  useEffect(() => {
    loadContacts();
    loadRoles();
    loadAgencies();
    loadPeopleClients();
    loadPeopleTeams();
    loadWorkspaces();
  }, []);

  const loadWorkspaces = async () => {
    try {
      const response = await axios.get('/api/workspaces');
      setWorkspaces(response.data);
    } catch (error) {
      console.error('Error loading workspaces:', error);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await axios.get('/api/contact-roles');
      setRoles(response.data);
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };

  const loadAgencies = async () => {
    try {
      const response = await axios.get('/api/agencies');
      setAgencies(response.data);
    } catch (error) {
      console.error('Error loading agencies:', error);
    }
  };

  const loadPeopleClients = async () => {
    try {
      const response = await axios.get('/api/people-clients');
      setPeopleClients(response.data);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadPeopleTeams = async () => {
    try {
      const response = await axios.get('/api/people-teams');
      setPeopleTeams(response.data);
    } catch (error) {
      console.error('Error loading teams:', error);
    }
  };

  const loadContacts = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Error loading contacts:', error);
      alert('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    
    if (!newContact.name || !newContact.number) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const payload = {
        ...newContact,
        role_id: newContact.role_id || null,
        agency_id: newContact.agency_id || null,
        people_client_id: newContact.people_client_id || null,
        people_team_id: newContact.people_team_id || null,
        workspace_ids: newContact.workspace_ids || []
      };
      
      if (editingContact) {
        await axios.put(`/api/contacts/${editingContact.id}`, payload);
        alert('✅ Contact updated successfully!');
      } else {
        await axios.post('/api/contacts', payload);
        alert('✅ Contact added successfully!');
      }
      setNewContact({ name: '', number: '', email: '', role_id: '', agency_id: '', people_client_id: '', people_team_id: '', workspace_ids: [] });
      setEditingContact(null);
      setShowAddForm(false);
      loadContacts();
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('❌ Failed to save contact');
    }
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setNewContact({
      name: contact.name,
      number: contact.number || '',
      email: contact.email || '',
      role_id: contact.role_id || '',
      agency_id: contact.agency_id || '',
      people_client_id: contact.people_client_id || '',
      people_team_id: contact.people_team_id || '',
      workspace_ids: contact.workspaces ? contact.workspaces.map(w => w.id) : []
    });
    setShowAddForm(true);
  };

  const handleDelete = async (contactId, contactName) => {
    if (!window.confirm(`Delete contact "${contactName}"?`)) {
      return;
    }

    try {
      await axios.delete(`/api/contacts/${contactId}`);
      setContacts(contacts.filter(c => c.id !== contactId));
      alert('✅ Contact deleted successfully');
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('❌ Failed to delete contact');
    }
  };

  // Deterministically generate a gradient based on the contact name
  const getAvatarGradient = (name) => {
    if (!name) return 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)';
    const gradients = [
      'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', // Indigo/Violet
      'linear-gradient(135deg, #ec4899 0%, #db2777 100%)', // Pink
      'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', // Blue
      'linear-gradient(135deg, #10b981 0%, #047857 100%)', // Emerald
      'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', // Amber
      'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', // Purple
      'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', // Cyan
      'linear-gradient(135deg, #f43f5e 0%, #be123c 100%)', // Rose
    ];
    let sum = 0;
    for (let i = 0; i < name.length; i++) {
      sum += name.charCodeAt(i);
    }
    return gradients[sum % gradients.length];
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  };

  // Directory statistical aggregates
  const totalPeople = contacts.length;
  const activeAgencies = new Set(contacts.filter(c => c.agency_id).map(c => c.agency_id)).size;
  const activeClients = new Set(contacts.filter(c => c.people_client_id).map(c => c.people_client_id)).size;
  const activeTeams = new Set(contacts.filter(c => c.people_team_id).map(c => c.people_team_id)).size;

  // Filtered contacts list
  const filteredContacts = contacts.filter((contact) => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = 
      contact.name?.toLowerCase().includes(searchLower) ||
      contact.number?.toLowerCase().includes(searchLower) ||
      contact.email?.toLowerCase().includes(searchLower) ||
      contact.role_name?.toLowerCase().includes(searchLower) ||
      contact.agency_name?.toLowerCase().includes(searchLower) ||
      contact.client_name?.toLowerCase().includes(searchLower) ||
      contact.team_name?.toLowerCase().includes(searchLower);

    const matchesRole = !selectedRole || String(contact.role_id) === String(selectedRole);
    const matchesAgency = !selectedAgency || String(contact.agency_id) === String(selectedAgency);
    const matchesClient = !selectedClient || String(contact.people_client_id) === String(selectedClient);
    const matchesTeam = !selectedTeam || String(contact.people_team_id) === String(selectedTeam);

    return matchesSearch && matchesRole && matchesAgency && matchesClient && matchesTeam;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Dashboard Top Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e293b', fontWeight: '800', fontSize: '1.8rem', letterSpacing: '-0.02em' }}>People Directory</h2>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.95rem' }}>Manage team members, agency affiliations, and client connections</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="btn btn-primary"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
            color: 'white',
            padding: '12px 22px',
            borderRadius: '12px',
            border: 'none',
            fontWeight: '600',
            fontSize: '0.95rem',
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: '0 4px 10px rgba(79, 70, 229, 0.25)'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 14px rgba(79, 70, 229, 0.35)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 10px rgba(79, 70, 229, 0.25)';
          }}
        >
          <UserPlus size={18} />
          Add Person
        </button>
      </div>

      {/* Analytics Widget Grid */}
      <div className="people-stats-grid">
        <div className="people-stat-card">
          <div className="people-stat-icon-wrapper stat-grad-1">
            <User size={22} />
          </div>
          <div>
            <span style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Directory</span>
            <span style={{ fontSize: '1.6rem', fontWeight: '800', color: '#0f172a' }}>{totalPeople}</span>
          </div>
        </div>

        <div className="people-stat-card">
          <div className="people-stat-icon-wrapper stat-grad-2">
            <Briefcase size={22} />
          </div>
          <div>
            <span style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Agencies</span>
            <span style={{ fontSize: '1.6rem', fontWeight: '800', color: '#0f172a' }}>{activeAgencies}</span>
          </div>
        </div>

        <div className="people-stat-card">
          <div className="people-stat-icon-wrapper stat-grad-3">
            <Building2 size={22} />
          </div>
          <div>
            <span style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Clients Connected</span>
            <span style={{ fontSize: '1.6rem', fontWeight: '800', color: '#0f172a' }}>{activeClients}</span>
          </div>
        </div>

        <div className="people-stat-card">
          <div className="people-stat-icon-wrapper stat-grad-4">
            <UsersRound size={22} />
          </div>
          <div>
            <span style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Active Teams</span>
            <span style={{ fontSize: '1.6rem', fontWeight: '800', color: '#0f172a' }}>{activeTeams}</span>
          </div>
        </div>
      </div>

      {/* Modern Search & Filtering Toolbar */}
      <div className="people-toolbar">
        <div className="search-wrapper">
          <Search size={18} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Search by name, email, phone, role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input-premium"
          />
        </div>

        <div className="filters-wrapper">
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="filter-select-premium"
          >
            <option value="">All Roles</option>
            {roles.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>

          <select
            value={selectedAgency}
            onChange={(e) => setSelectedAgency(e.target.value)}
            className="filter-select-premium"
          >
            <option value="">All Agencies</option>
            {agencies.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>

          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="filter-select-premium"
          >
            <option value="">All Clients</option>
            {peopleClients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <select
            value={selectedTeam}
            onChange={(e) => setSelectedTeam(e.target.value)}
            className="filter-select-premium"
          >
            <option value="">All Teams</option>
            {peopleTeams.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>

          {(searchQuery || selectedRole || selectedAgency || selectedClient || selectedTeam) && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedRole('');
                setSelectedAgency('');
                setSelectedClient('');
                setSelectedTeam('');
              }}
              style={{
                background: '#f1f5f9',
                border: 'none',
                color: '#475569',
                padding: '8px 14px',
                borderRadius: '10px',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#e2e8f0';
                e.currentTarget.style.color = '#1e293b';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = '#f1f5f9';
                e.currentTarget.style.color = '#475569';
              }}
            >
              <RotateCcw size={14} />
              Reset Filters
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #f1f5f9', textAlign: 'center', padding: '60px 40px', color: '#64748b' }}>
          <RefreshCw size={32} className="spinning" style={{ marginBottom: '14px', color: '#4f46e5' }} />
          <p style={{ fontWeight: '500' }}>Synchronizing directories...</p>
        </div>
      ) : filteredContacts.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '80px 40px', 
          color: '#64748b',
          background: 'white',
          borderRadius: '16px',
          border: '1px solid #f1f5f9',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <AlertCircle size={48} style={{ color: '#94a3b8', marginBottom: '16px', opacity: 0.8 }} />
          <h3 style={{ color: '#1e293b', fontSize: '1.2rem', fontWeight: '700', margin: '0 0 8px 0' }}>
            {contacts.length === 0 ? 'No People Found' : 'No Matching Connections'}
          </h3>
          <p style={{ fontSize: '0.95rem', margin: 0, color: '#64748b' }}>
            {contacts.length === 0 
              ? 'Begin by populating your organization directory.' 
              : 'Try adjusting your search criteria or resetting filters.'}
          </p>
          {contacts.length === 0 && (
            <button
              onClick={() => setShowAddForm(true)}
              className="btn btn-primary"
              style={{
                marginTop: '20px',
                background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '10px',
                color: 'white',
                fontWeight: '600'
              }}
            >
              Add First Contact
            </button>
          )}
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '4px', color: '#64748b', fontWeight: '600', fontSize: '0.9rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px' }}>
            <span>Displaying {filteredContacts.length} of {totalPeople} connection{totalPeople !== 1 ? 's' : ''}</span>
          </div>
          <div className="people-table-container">
            <table className="people-table-premium">
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ width: '25%' }}>Identity</th>
                  <th style={{ width: '18%' }}>Phone</th>
                  <th style={{ width: '22%' }}>Email Address</th>
                  <th style={{ width: '15%' }}>Workspaces</th>
                  <th style={{ width: '15%' }}>Agency</th>
                  <th style={{ width: '15%' }}>Aligned Context</th>
                  <th style={{ width: '5%', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map((contact) => (
                  <tr key={contact.id} className="table-row-premium">
                    {/* IDENTITY */}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div 
                          className="avatar-gradient" 
                          style={{ background: getAvatarGradient(contact.name) }}
                        >
                          {getInitials(contact.name)}
                        </div>
                        <div>
                          <span style={{ display: 'block', fontWeight: '600', color: '#1e293b', fontSize: '0.95rem' }}>{contact.name}</span>
                          {contact.role_name ? (
                            <span style={{
                              fontSize: '0.78rem',
                              fontWeight: '600',
                              color: contact.role_color || '#475569',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              marginTop: '2px'
                            }}>
                              <Tag size={10} />
                              {contact.role_name}
                            </span>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>No role assigned</span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* NUMBER */}
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '0.9rem' }}>
                        <Phone size={14} style={{ color: '#94a3b8' }} />
                        <a href={`tel:${contact.number}`} style={{ color: 'inherit', textDecoration: 'none', fontWeight: '500' }}>
                          {contact.number || '—'}
                        </a>
                      </div>
                    </td>

                    {/* EMAIL */}
                    <td>
                      {contact.email ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '0.9rem' }}>
                          <Mail size={14} style={{ color: '#94a3b8' }} />
                          <a href={`mailto:${contact.email}`} style={{ color: 'inherit', textDecoration: 'none', wordBreak: 'break-all', fontWeight: '500' }}>
                            {contact.email}
                          </a>
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>
                      )}
                    </td>

                    {/* WORKSPACES */}
                    <td>
                      {contact.workspaces && contact.workspaces.length > 0 ? (
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {contact.workspaces.map((ws) => (
                            <span key={ws.id} style={{
                              padding: '2px 8px',
                              borderRadius: '6px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              background: '#e0e7ff',
                              color: '#4f46e5',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              {ws.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>
                      )}
                    </td>

                    {/* AGENCY */}
                    <td>
                      {contact.agency_name ? (
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '8px',
                          fontSize: '0.78rem',
                          fontWeight: '600',
                          background: '#fffbeb',
                          color: '#b45309',
                          border: '1px solid #fde68a',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <Briefcase size={11} />
                          {contact.agency_name}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>
                      )}
                    </td>

                    {/* CLIENT & TEAM */}
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {contact.client_name ? (
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '8px',
                            fontSize: '0.78rem',
                            fontWeight: '600',
                            background: '#eff6ff',
                            color: '#1d4ed8',
                            border: '1px solid #bfdbfe',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            width: 'fit-content'
                          }}>
                            <Building2 size={11} />
                            {contact.client_name}
                          </span>
                        ) : null}

                        {contact.team_name ? (
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '8px',
                            fontSize: '0.78rem',
                            fontWeight: '600',
                            background: '#f0fdf4',
                            color: '#15803d',
                            border: '1px solid #bbf7d0',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            width: 'fit-content'
                          }}>
                            <UsersRound size={11} />
                            {contact.team_name}
                          </span>
                        ) : null}

                        {!contact.client_name && !contact.team_name ? (
                          <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>
                        ) : null}
                      </div>
                    </td>

                    {/* ACTIONS */}
                    <td>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleEditContact(contact)}
                          className="action-btn-circle edit"
                          title="Edit Details"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(contact.id, contact.name)}
                          className="action-btn-circle delete"
                          title="Delete Connection"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Drawer Panel Container */}
      {showAddForm && (
        <div className="drawer-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowAddForm(false);
            setEditingContact(null);
            setNewContact({ name: '', number: '', email: '', role_id: '', agency_id: '', people_client_id: '', people_team_id: '', workspace_ids: [] });
          }
        }}>
          <div className="drawer-content">
            {/* Drawer Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '24px',
              borderBottom: '1px solid #f1f5f9',
              background: 'linear-gradient(to right, #faf5ff, white)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '12px',
                  background: editingContact ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                }}>
                  {editingContact ? <Edit2 size={18} color="#b45309" /> : <UserPlus size={18} color="#4338ca" />}
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#0f172a' }}>
                    {editingContact ? 'Edit Profile' : 'Add New Person'}
                  </h3>
                  <p style={{ margin: '2px 0 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                    {editingContact ? `Modify profile for ${editingContact.name}` : 'Create a new organizational record'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setEditingContact(null);
                  setNewContact({ name: '', number: '', email: '', role_id: '', agency_id: '', people_client_id: '', people_team_id: '', workspace_ids: [] });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748b',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                onMouseOut={(e) => e.currentTarget.style.background = 'none'}
              >
                <X size={20} />
              </button>
            </div>

            {/* Drawer Body - Form */}
            <form onSubmit={handleAddContact} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div style={{ padding: '24px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                
                {/* Name Input */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '700', fontSize: '0.85rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    Full Name <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                      placeholder="e.g., Jane Doe"
                      required
                      style={{ 
                        width: '100%', 
                        padding: '12px 14px', 
                        borderRadius: '10px', 
                        border: '1.5px solid #cbd5e1',
                        fontSize: '0.95rem',
                        outline: 'none',
                        background: '#f8fafc',
                        transition: 'all 0.2s'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#6366f1';
                        e.target.style.background = 'white';
                        e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.15)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#cbd5e1';
                        e.target.style.background = '#f8fafc';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                </div>

                {/* Grid for Phone & Email */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '700', fontSize: '0.85rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Phone Number <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                      type="tel"
                      value={newContact.number}
                      onChange={(e) => setNewContact({ ...newContact, number: e.target.value })}
                      placeholder="e.g., 5511999999999"
                      required
                      style={{ 
                        width: '100%', 
                        padding: '12px 14px', 
                        borderRadius: '10px', 
                        border: '1.5px solid #cbd5e1',
                        fontSize: '0.95rem',
                        outline: 'none',
                        background: '#f8fafc',
                        transition: 'all 0.2s'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#6366f1';
                        e.target.style.background = 'white';
                        e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.15)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#cbd5e1';
                        e.target.style.background = '#f8fafc';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '700', fontSize: '0.85rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                      placeholder="e.g., jane@company.com"
                      style={{ 
                        width: '100%', 
                        padding: '12px 14px', 
                        borderRadius: '10px', 
                        border: '1.5px solid #cbd5e1',
                        fontSize: '0.95rem',
                        outline: 'none',
                        background: '#f8fafc',
                        transition: 'all 0.2s'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#6366f1';
                        e.target.style.background = 'white';
                        e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.15)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#cbd5e1';
                        e.target.style.background = '#f8fafc';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                </div>

                {/* Role & Agency Selection */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '700', fontSize: '0.85rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Designated Role
                    </label>
                    <select
                      value={newContact.role_id}
                      onChange={(e) => setNewContact({ ...newContact, role_id: e.target.value })}
                      style={{ 
                        width: '100%', 
                        padding: '12px 14px', 
                        borderRadius: '10px', 
                        border: '1.5px solid #cbd5e1',
                        fontSize: '0.95rem',
                        outline: 'none',
                        background: '#f8fafc url("data:image/svg+xml,%3Csvg width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L5 5L9 1\' stroke=\'%2364748B\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") no-repeat right 12px center',
                        appearance: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#6366f1';
                        e.target.style.background = 'white url("data:image/svg+xml,%3Csvg width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L5 5L9 1\' stroke=\'%236366F1\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") no-repeat right 12px center';
                        e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.15)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#cbd5e1';
                        e.target.style.background = '#f8fafc url("data:image/svg+xml,%3Csvg width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L5 5L9 1\' stroke=\'%2364748B\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") no-repeat right 12px center';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="">Select a role...</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '700', fontSize: '0.85rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Affiliated Agency
                    </label>
                    <select
                      value={newContact.agency_id}
                      onChange={(e) => setNewContact({ ...newContact, agency_id: e.target.value })}
                      style={{ 
                        width: '100%', 
                        padding: '12px 14px', 
                        borderRadius: '10px', 
                        border: '1.5px solid #cbd5e1',
                        fontSize: '0.95rem',
                        outline: 'none',
                        background: '#f8fafc url("data:image/svg+xml,%3Csvg width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L5 5L9 1\' stroke=\'%2364748B\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") no-repeat right 12px center',
                        appearance: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#6366f1';
                        e.target.style.background = 'white url("data:image/svg+xml,%3Csvg width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L5 5L9 1\' stroke=\'%236366F1\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") no-repeat right 12px center';
                        e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.15)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#cbd5e1';
                        e.target.style.background = '#f8fafc url("data:image/svg+xml,%3Csvg width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L5 5L9 1\' stroke=\'%2364748B\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") no-repeat right 12px center';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="">Select an agency...</option>
                      {agencies.map((agency) => (
                        <option key={agency.id} value={agency.id}>{agency.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Client & Team Assignment */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '700', fontSize: '0.85rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Client Alignment
                    </label>
                    <select
                      value={newContact.people_client_id}
                      onChange={(e) => setNewContact({ ...newContact, people_client_id: e.target.value })}
                      style={{ 
                        width: '100%', 
                        padding: '12px 14px', 
                        borderRadius: '10px', 
                        border: '1.5px solid #cbd5e1',
                        fontSize: '0.95rem',
                        outline: 'none',
                        background: '#f8fafc url("data:image/svg+xml,%3Csvg width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L5 5L9 1\' stroke=\'%2364748B\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") no-repeat right 12px center',
                        appearance: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#6366f1';
                        e.target.style.background = 'white url("data:image/svg+xml,%3Csvg width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L5 5L9 1\' stroke=\'%236366F1\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") no-repeat right 12px center';
                        e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.15)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#cbd5e1';
                        e.target.style.background = '#f8fafc url("data:image/svg+xml,%3Csvg width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L5 5L9 1\' stroke=\'%2364748B\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") no-repeat right 12px center';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="">Select a client...</option>
                      {peopleClients.map((client) => (
                        <option key={client.id} value={client.id}>{client.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: '700', fontSize: '0.85rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Assigned Team
                    </label>
                    <select
                      value={newContact.people_team_id}
                      onChange={(e) => setNewContact({ ...newContact, people_team_id: e.target.value })}
                      style={{ 
                        width: '100%', 
                        padding: '12px 14px', 
                        borderRadius: '10px', 
                        border: '1.5px solid #cbd5e1',
                        fontSize: '0.95rem',
                        outline: 'none',
                        background: '#f8fafc url("data:image/svg+xml,%3Csvg width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L5 5L9 1\' stroke=\'%2364748B\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") no-repeat right 12px center',
                        appearance: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#6366f1';
                        e.target.style.background = 'white url("data:image/svg+xml,%3Csvg width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L5 5L9 1\' stroke=\'%236366F1\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") no-repeat right 12px center';
                        e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.15)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#cbd5e1';
                        e.target.style.background = '#f8fafc url("data:image/svg+xml,%3Csvg width=\'10\' height=\'6\' viewBox=\'0 0 10 6\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M1 1L5 5L9 1\' stroke=\'%2364748B\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") no-repeat right 12px center';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="">Select a team...</option>
                      {peopleTeams.map((team) => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ marginTop: '24px', borderTop: '1px solid #f1f5f9', paddingTop: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '700', fontSize: '0.85rem', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                    Workspace Assignments
                  </label>
                  {workspaces.length === 0 ? (
                    <span style={{ fontSize: '0.85rem', color: '#64748b' }}>No workspaces available.</span>
                  ) : (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '10px',
                      padding: '12px 16px',
                      background: '#f8fafc',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      maxHeight: '150px',
                      overflowY: 'auto'
                    }}>
                      {workspaces.map((ws) => {
                        const isChecked = newContact.workspace_ids && newContact.workspace_ids.includes(ws.id);
                        return (
                          <label key={ws.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            color: '#475569',
                            cursor: 'pointer',
                            padding: '4px 6px',
                            borderRadius: '6px',
                            transition: 'background 0.2s'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.background = '#eef2ff'}
                          onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked || false}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setNewContact(prev => {
                                  const currentIds = prev.workspace_ids || [];
                                  const nextIds = checked
                                    ? [...currentIds, ws.id]
                                    : currentIds.filter(id => id !== ws.id);
                                  return { ...prev, workspace_ids: nextIds };
                                });
                              }}
                              style={{
                                width: '16px',
                                height: '16px',
                                accentColor: '#6366f1',
                                cursor: 'pointer'
                              }}
                            />
                            <span>{ws.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

              </div>

              {/* Drawer Footer controls */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                padding: '20px 24px',
                borderTop: '1px solid #f1f5f9',
                background: '#f8fafc'
              }}>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingContact(null);
                    setNewContact({ name: '', number: '', email: '', role_id: '', agency_id: '', people_client_id: '', people_team_id: '', workspace_ids: [] });
                  }}
                  style={{
                    padding: '12px 20px',
                    background: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    color: '#475569',
                    fontSize: '0.9rem',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  style={{
                    padding: '12px 24px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                    color: 'white',
                    border: 'none',
                    fontWeight: '700',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 10px rgba(79, 70, 229, 0.2)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 14px rgba(79, 70, 229, 0.3)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 10px rgba(79, 70, 229, 0.2)';
                  }}
                >
                  {editingContact ? 'Save Updates' : 'Register Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactsManager;
